import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  Avatar,
  Button,
  Chip,
  Icon,
  IconButton,
  Menu,
  RoleChip,
  Segmented,
  useToast,
} from '@w7/ui'
import { activeNavGroupId, isNavItemActive, type NavItem } from './nav'
import { useConsoleNav } from './useConsoleNav'
import { useSession } from './queries'
import { useDateRange, RANGE_PRESETS } from './range'
import { ROLE_LABEL } from '@/data/types'
import { displayMoneyCompact, money } from '@/lib/format'
import { availableToGive } from '@/lib/book'
import { beginChangePassword, isDemoSession, logout, claims } from '@/auth/oidc'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { canChangePassword, canManageGoogleAuth } from './staffAccess'
import { toastCopy } from '@/ui/toaster'
import { SearchTrigger } from '@/features/search'
import { StrixMark } from '@/features/auth/StrixMark'
import { useThemePrefs } from './theme'

const RAIL_COLLAPSED_KEY = 'w7.rail.collapsed'
const RAIL_OPEN_SECTIONS_KEY = 'w7.rail.sections'

type MiniNavTip = { label: string; description: string; top: number; left: number }

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

/** Below this the rail is a drawer, and a drawer always shows full labels. */
const RAIL_DRAWER_QUERY = '(max-width: 900px)'

function useIsRailDrawer(): boolean {
  const [drawer, setDrawer] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia(RAIL_DRAWER_QUERY)
    const sync = () => setDrawer(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  return drawer
}

function readOpenSections(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(RAIL_OPEN_SECTIONS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

/**
 * The authenticated shell.
 *
 * Rail is viewport-locked (does not scroll with page content). The nav list
 * scrolls only when items overflow — typical on short viewports. Desktop can
 * collapse to icon-only with tooltips; preference persists in localStorage.
 */

/**
 * One rail row.
 *
 * Book Management owns the books dropdown. The whole parent row is one button,
 * not a link with a separate hit area glued to it — a parent that both
 * navigates and expands makes every click a coin toss, and the pages an
 * operator wants are the children anyway.
 */
function NavRow({
  item,
  collapsed,
  iconOnly,
  pathname,
  open,
  onToggle,
  onShowTip,
  onHideTip,
}: {
  item: NavItem
  collapsed: boolean
  /** Icon-only desktop rail — children have nowhere to render. */
  iconOnly: boolean
  pathname: string
  open: boolean
  onToggle: (id: string) => void
  onShowTip?: (item: NavItem, el: HTMLElement) => void
  onHideTip?: () => void
}) {
  const active = isNavItemActive(pathname, item)

  if (!item.children?.length) {
    return (
      <Link
        to={item.to}
        className="w7-navitem"
        title={collapsed ? `${item.label} — ${item.description}` : item.description}
        aria-label={collapsed ? item.label : undefined}
        aria-current={active ? 'page' : undefined}
        onMouseEnter={(event) => onShowTip?.(item, event.currentTarget)}
        onMouseLeave={() => onHideTip?.()}
      >
        <span className="w7-navitem-icon" aria-hidden="true">
          <Icon name={item.icon} size={18} />
        </span>
        <span className="w7-navitem-label">{item.label}</span>
      </Link>
    )
  }

  const listId = `w7-nav-${item.id}`
  const expanded = open && !iconOnly

  return (
    <div className="w7-navsection" data-open={expanded ? 'true' : undefined}>
      <button
        type="button"
        className="w7-navitem w7-navsection-head"
        aria-expanded={expanded}
        aria-controls={listId}
        data-current={active ? 'true' : undefined}
        title={collapsed ? `${item.label} — ${item.description}` : item.description}
        onClick={() => onToggle(item.id)}
        onMouseEnter={(event) => onShowTip?.(item, event.currentTarget)}
        onMouseLeave={() => onHideTip?.()}
      >
        <span className="w7-navitem-icon" aria-hidden="true">
          <Icon name={item.icon} size={18} />
        </span>
        <span className="w7-navitem-label">{item.label}</span>
        <Icon name="chevron-down" size={14} className="w7-navsection-caret" aria-hidden="true" />
      </button>

      <div className="w7-navsub" id={listId} hidden={!expanded}>
        {item.children.map((child) => (
          <NavLink key={child.id} to={child.to} className="w7-navsub-item" end>
            <span className="w7-navsub-label">{child.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  )
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const nav = useConsoleNav()
  const [railOpen, setRailOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  /** Accordion: at most one multi-item group open (null until path resolves). */
  const [openGroupId, setOpenGroupId] = useState<string | null>(null)
  const [miniTip, setMiniTip] = useState<MiniNavTip | null>(null)
  // Book Management mode dropdowns — read once so restore does not race persist.
  const [openSections, setOpenSections] = useState<string[]>(readOpenSections)
  const drawer = useIsRailDrawer()
  const iconOnly = collapsed && !drawer
  const activeGroupId = useMemo(
    () => activeNavGroupId(nav, location.pathname),
    [nav, location.pathname],
  )

  useEffect(() => {
    setCollapsed(readCollapsedPreference())
  }, [])

  // The book workspace holding the current page stays open under its group.
  const currentSection = nav
    .flatMap((group) => group.items)
    .find((item) => item.children?.length && isNavItemActive(location.pathname, item))?.id

  useEffect(() => {
    if (!currentSection) return
    setOpenSections((ids) => (ids.includes(currentSection) ? ids : [...ids, currentSection]))
  }, [currentSection])

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_OPEN_SECTIONS_KEY, JSON.stringify(openSections))
    } catch {
      /* private mode / blocked storage — preference is session-only */
    }
  }, [openSections])

  const toggleSection = (id: string) => {
    // Collapsing the rail to icons hides the children; a click on the icon is
    // asking to see them, so restore the full rail rather than doing nothing.
    if (iconOnly) {
      setCollapsed(false)
      setOpenSections((ids) => (ids.includes(id) ? ids : [...ids, id]))
      return
    }
    setOpenSections((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))
  }

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* private mode / blocked storage — preference is session-only */
    }
    if (!collapsed) setMiniTip(null)
  }, [collapsed])

  // Any navigation closes the mobile rail — leaving it open over the new page
  // is the single most common phone-nav bug.
  useEffect(() => {
    setRailOpen(false)
    setMiniTip(null)
  }, [location.pathname])

  // Open the section for the current page (closes any other).
  useEffect(() => {
    if (activeGroupId) setOpenGroupId(activeGroupId)
  }, [activeGroupId])

  const toggleGroup = (id: string) => {
    setOpenGroupId((prev) => (prev === id ? null : id))
  }

  const toggleCollapsed = () => setCollapsed((value) => !value)

  return (
    <div className="w7-app">
      <a href="#main" className="w7-skip">
        Skip to content
      </a>

      <aside
        className="w7-rail"
        data-open={railOpen ? 'true' : undefined}
        data-collapsed={collapsed ? 'true' : undefined}
        aria-label="Console navigation"
      >
        <div className="w7-rail-head">
          <Link to="/overview" className="strix-mark-link" aria-label="Strix — overview">
            <StrixMark size={collapsed ? 28 : 26} showName={!collapsed} />
          </Link>
          <IconButton
            name="menu"
            label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="w7-rail-collapse"
            onClick={toggleCollapsed}
          />
          <IconButton
            name="close"
            label="Close menu"
            className="w7-rail-close"
            onClick={() => setRailOpen(false)}
          />
        </div>

        <nav className="w7-rail-scroll" aria-label="Console sections">
          {nav.map((group) => {
            const open = openGroupId === group.id
            const leadIcon = group.icon ?? group.items[0]?.icon ?? 'overview'
            const showMiniTip = (item: NavItem, el: HTMLElement) => {
              if (!collapsed || drawer) return
              const box = el.getBoundingClientRect()
              setMiniTip({
                label: group.items.length === 1 ? group.label : item.label,
                description: item.description,
                top: box.top + box.height / 2,
                left: box.right + 10,
              })
            }
            // Solo only for flat single links — Book Management keeps its modes.
            const solo =
              group.items.length === 1 && !group.items[0]?.children?.length

            if (solo) {
              const item = group.items[0]!
              return (
                <div key={group.id} className="w7-navgroup" data-solo="true">
                  <NavLink
                    to={item.to}
                    end={Boolean(item.match)}
                    className="w7-navitem w7-navgroup-solo"
                    title={
                      collapsed ? `${group.label} — ${item.description}` : item.description
                    }
                    aria-label={collapsed ? group.label : undefined}
                    onMouseEnter={(event) => showMiniTip(item, event.currentTarget)}
                    onMouseLeave={() => setMiniTip(null)}
                  >
                    <span className="w7-navitem-icon" aria-hidden="true">
                      <Icon name={item.icon} size={18} />
                    </span>
                    <span className="w7-navitem-label">{group.label}</span>
                  </NavLink>
                </div>
              )
            }

            return (
              <div
                key={group.id}
                className="w7-navgroup"
                data-open={open ? 'true' : 'false'}
              >
                <button
                  type="button"
                  className="w7-navgroup-toggle"
                  aria-expanded={open}
                  onClick={() => toggleGroup(group.id)}
                >
                  <span className="w7-navgroup-toggle-icon" aria-hidden="true">
                    <Icon name={leadIcon} size={18} />
                  </span>
                  <span className="w7-navgroup-toggle-label">{group.label}</span>
                  <span className="w7-navgroup-toggle-chevron" aria-hidden="true">
                    <Icon name="chevron-down" size={14} />
                  </span>
                </button>
                <div className="w7-navgroup-items">
                  <div className="w7-navgroup-items-inner">
                    {group.items.flatMap((item) => {
                      // Same label as the accordion (Book Management → Book Management)
                      // would double the row — flatten modes into the group.
                      if (item.children?.length && item.label === group.label) {
                        return item.children.map((child) => {
                          const childIcon = child.icon ?? item.icon
                          const tipItem: NavItem = {
                            id: child.id,
                            label: child.label,
                            to: child.to,
                            icon: childIcon,
                            description: child.label,
                            match: child.to,
                            // Legacy /net-exposure alias lived on the workspace parent.
                            aliases:
                              child.id === 'net-exposure' ? ['/net-exposure'] : undefined,
                          }
                          const active = isNavItemActive(location.pathname, tipItem)
                          return (
                            <NavLink
                              key={child.id}
                              to={child.to}
                              className="w7-navitem"
                              title={
                                collapsed
                                  ? `${child.label} — ${item.description}`
                                  : child.label
                              }
                              aria-label={collapsed ? child.label : undefined}
                              aria-current={active ? 'page' : undefined}
                              onMouseEnter={(event) =>
                                showMiniTip(tipItem, event.currentTarget)
                              }
                              onMouseLeave={() => setMiniTip(null)}
                            >
                              <span className="w7-navitem-icon" aria-hidden="true">
                                <Icon name={childIcon} size={18} />
                              </span>
                              <span className="w7-navitem-label">{child.label}</span>
                            </NavLink>
                          )
                        })
                      }
                      return [
                        <NavRow
                          key={item.id}
                          item={item}
                          collapsed={collapsed}
                          iconOnly={iconOnly}
                          pathname={location.pathname}
                          open={openSections.includes(item.id)}
                          onToggle={toggleSection}
                          onShowTip={showMiniTip}
                          onHideTip={() => setMiniTip(null)}
                        />,
                      ]
                    })}
                  </div>
                </div>
              </div>
            )
          })}
        </nav>

        <div
          className="w7-rail-foot"
          title={
            isDemoSession()
              ? 'Every figure here is generated, not from the ledger. Wired to live services this becomes real without a UI change.'
              : 'Figures come from hierarchy and analytics for your agentId. Demo login still uses fixtures.'
          }
        >
          <Chip
            tone={isDemoSession() ? 'warn' : 'profit'}
            size="sm"
            icon={isDemoSession() ? 'info' : 'live'}
            className="w7-rail-session"
          >
            {isDemoSession() ? 'Demo data' : 'Live session'}
          </Chip>
          <p className="w7-rail-session-hint w7-dim">
            {isDemoSession()
              ? 'Every figure here is generated, not from the ledger.'
              : 'Figures come from hierarchy and analytics for your agentId.'}
          </p>
        </div>
      </aside>

      {/* Its OWN scrim class, not the modal one. The shared `w7-scrim` sits at
          z-index 100 so it can cover a drawer at 101; the mobile rail is at 60,
          so reusing it put the scrim ON TOP of the open menu and swallowed every
          tap — the menu opened and then did nothing. */}
      {railOpen ? (
        <div className="w7-scrim w7-scrim-nav" onClick={() => setRailOpen(false)} />
      ) : null}

      {collapsed && !drawer && miniTip ? (
        <div
          className="w7-navitem-flyout"
          role="tooltip"
          style={{ top: miniTip.top, left: miniTip.left }}
        >
          <span className="w7-navitem-flyout-title">{miniTip.label}</span>
          <span className="w7-navitem-flyout-desc">{miniTip.description}</span>
        </div>
      ) : null}

      <div className="w7-main">
        <Topbar onOpenRail={() => setRailOpen(true)} />
        <main className="w7-content" id="main">
          {children}
        </main>
      </div>
    </div>
  )
}

/**
 * Topbar day filter — only where the page reads `useDateRange()`.
 *
 * SHOW: Overview, Downline, Member (shell range drives their queries).
 * HIDE everywhere else: reports own a picker; create / settings / MFA are not
 * day-scoped; exposure + net-exposure + activity use live or local filters.
 */
const SHOW_TOPBAR_RANGE = /^\/(?:overview(?:\/|$)|downline(?:\/|$)|member(?:\/|$))/

function Topbar({ onOpenRail }: { onOpenRail: () => void }) {
  const { data: session } = useSession()
  const { data: staffMe } = useStaffMe()
  const { range, preset, setPreset } = useDateRange()
  const { pathname } = useLocation()
  const toast = useToast()
  const showRange = SHOW_TOPBAR_RANGE.test(pathname)
  const { openPreferences } = useThemePrefs()

  const account = session?.account
  const headroom = account ? availableToGive(account.balance, account.exposure) : null
  const demo = isDemoSession()
  const who = claims()
  const signedIn = Boolean(who || demo)
  const signedInAs =
    who?.preferredUsername || who?.email || (demo ? 'demo' : account?.username) || 'Signed in'
  const roleLabel = account ? ROLE_LABEL[account.role] : undefined
  const showChangePassword = canChangePassword(staffMe)
  const showGoogleAuth = canManageGoogleAuth(staffMe)

  const changePassword = () => {
    if (demo) {
      toast.push(toastCopy.passwordNeedKeycloak)
      return
    }
    void beginChangePassword().catch((err) => {
      toast.push(
        toastCopy.passwordChangeError(err instanceof Error ? err.message : undefined),
      )
    })
  }

  return (
    <header className="w7-topbar">
      <IconButton name="menu" label="Open menu" className="w7-rail-toggle" onClick={onOpenRail} />

      {showRange ? (
        <div className="w7-topbar-dates">
          <Segmented
            label="Date range"
            value={preset}
            onChange={setPreset}
            items={RANGE_PRESETS.map((p) => ({ id: p.id, label: p.short }))}
          />
          <span className="w7-topbar-range w7-dim">{range.label}</span>
        </div>
      ) : null}

      {/* The palette's discoverability. Placed before the spacer so it sits with
          the workspace controls rather than the account trail — it is a way to
          get somewhere, not a setting. Collapses to the magnifier below 900px. */}
      <SearchTrigger />

      <span className="w7-spacer" />

      <div className="w7-topbar-trail">
        <div
          className="w7-wallet"
          title={
            account
              ? `Balance ${money(account.balance, { currency: account.currencyType, alreadyDisplay: true })} · Exposure ${money(account.exposure, { currency: account.currencyType, alreadyDisplay: true })}`
              : undefined
          }
        >
          <Icon name="wallet" size={16} />
          <span>
            <b>
              {headroom === null
                ? '—'
                : displayMoneyCompact(headroom, account?.currencyType)}
            </b>
            <em>free to give</em>
          </span>
        </div>

        {signedIn || account ? (
          <Menu
            label="Account"
            align="end"
            trigger={<Avatar name={signedInAs} />}
            header={
              <div className="w7-menu-who">
                <Avatar name={signedInAs} size="sm" />
                <div className="w7-menu-who-text">
                  <span className="w7-menu-who-name w7-truncate" title={signedInAs}>
                    {signedInAs}
                  </span>
                  {roleLabel ? <span className="w7-menu-who-role">{roleLabel}</span> : null}
                </div>
              </div>
            }
            items={[
              ...(showChangePassword
                ? [
                    {
                      id: 'password',
                      label: 'Change password',
                      icon: 'lock' as const,
                      onSelect: changePassword,
                    },
                  ]
                : []),
              ...(showGoogleAuth
                ? [
                    {
                      id: '2fa',
                      label: 'Two-factor authentication',
                      icon: 'shield' as const,
                      onSelect: () => {
                        // Hard navigate so a stale SPA shell cannot keep an old Keycloak assign.
                        window.location.assign(`${window.location.origin}/settings/security`)
                      },
                    },
                  ]
                : []),
              {
                id: 'version',
                label: 'App version',
                icon: 'settings',
                onSelect: () => {
                  window.location.assign(`${window.location.origin}/settings/version`)
                },
              },
              {
                id: 'prefs',
                label: 'Appearance',
                icon: 'sliders',
                onSelect: () => openPreferences(),
              },
              'separator',
              {
                id: 'out',
                label: 'Sign out',
                icon: 'logout',
                tone: 'danger',
                onSelect: () => logout(),
              },
            ]}
          />
        ) : null}
      </div>
    </header>
  )
}

/** Identity block reused by the downline table and the member header. */
export function AccountCell({
  username,
  displayName,
  role,
  to,
  meta,
}: {
  username: string
  displayName: string
  role: string
  to?: string
  meta?: React.ReactNode
}) {
  const body = (
    <>
      <Avatar name={displayName || username} size="sm" />
      <span style={{ minWidth: 0 }}>
        <span className="w7-row" style={{ gap: 6 }}>
          <span className="w7-truncate" style={{ fontWeight: 600 }}>
            {username}
          </span>
          <RoleChip role={role} label={ROLE_LABEL[role as keyof typeof ROLE_LABEL] ?? role} />
        </span>
        <span className="w7-dim w7-truncate" style={{ display: 'block', fontSize: 11.5 }}>
          {meta ?? displayName}
        </span>
      </span>
    </>
  )

  return to ? (
    <Link to={to} className="w7-identity">
      {body}
    </Link>
  ) : (
    <span className="w7-identity">{body}</span>
  )
}

export { Button }
